/**
 * Workflow Version History Hook (Repository Pattern Compliant)
 *
 * React hook for accessing draft version history via Repository pattern.
 * Follows FERP architectural mandate: Repository pattern for ALL data access.
 *
 * Architecture:
 * - Repository Pattern: All GraphQL access through WorkflowDraftRepository
 * - Audit Trails: Complete logging via BaseRepository
 * - Safety Validation: Life-critical operation validation
 * - Domain-Agnostic: No hardcoded business logic
 *
 * Life-Critical: Production-ready with complete error handling
 * Compliance: HIPAA/SOX/FDA audit trail requirements
 *
 * @see CLAUDE.md Line 24: "Repository pattern mandatory - All data access through repositories"
 * @see WorkflowDraftRepository.ts for repository implementation
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import WorkflowDraftRepository, { type DraftVersion } from '../data/repositories/WorkflowDraftRepository';
import { useCurrentTenant } from './useCurrentTenant';

export interface UseWorkflowVersionHistoryOptions {
  /** Draft kind (e.g., 'WorkflowDefinition', 'Instance') */
  kind?: string;
  /** Workflow key to load versions for */
  workflowKey?: string;
  /** Maximum number of versions to load */
  limit?: number;
  /** Auto-refresh interval in milliseconds (0 = disabled) */
  refreshInterval?: number;
  /** Enable auto-refresh (default: false) */
  enableAutoRefresh?: boolean;
}

export interface UseWorkflowVersionHistoryReturn {
  /** List of draft versions */
  versions: DraftVersion[];
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Selected workflow key */
  selectedWorkflowKey: string;
  /** Set selected workflow key */
  setSelectedWorkflowKey: (key: string) => void;
  /** Refresh versions manually */
  refresh: () => Promise<void>;
  /** Repository instance (for advanced operations) */
  repository: WorkflowDraftRepository;
}

/**
 * Hook for accessing workflow version history via Repository pattern
 *
 * @example
 * ```tsx
 * const { versions, loading, error, selectedWorkflowKey, setSelectedWorkflowKey } = useWorkflowVersionHistory({
 *   kind: 'WorkflowDefinition',
 *   workflowKey: 'patient-registration-v1',
 *   limit: 50,
 * });
 * ```
 */
export function useWorkflowVersionHistory(
  options: UseWorkflowVersionHistoryOptions = {}
): UseWorkflowVersionHistoryReturn {
  const {
    kind = 'WorkflowDefinition',
    workflowKey,
    limit = 50,
    refreshInterval = 0,
    enableAutoRefresh = false,
  } = options;

  // Get tenant from context (data-driven architecture)
  const { tenantId: tenant } = useCurrentTenant();

  // Create repository instance (memoized to prevent recreating on every render)
  const repository = useMemo(
    () => new WorkflowDraftRepository(),
    []
  );

  // State management
  const [selectedWorkflowKey, setSelectedWorkflowKey] = useState<string>(workflowKey || '');
  const [versions, setVersions] = useState<DraftVersion[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load version history from repository
   * Includes audit trail and safety validation (handled by repository)
   */
  const loadVersions = useCallback(async () => {
    if (!tenant || !selectedWorkflowKey) {
      setVersions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // ✅ REPOSITORY PATTERN: Use repository method
      const result = await repository.getDraftVersions(tenant, kind, selectedWorkflowKey, limit);

      setVersions(result);
      setError(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[useWorkflowVersionHistory] Failed to load versions:', error);
      setError(error);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [tenant, kind, selectedWorkflowKey, limit, repository]);

  /**
   * Refresh versions manually
   */
  const refresh = useCallback(async () => {
    await loadVersions();
  }, [loadVersions]);

  // Load versions when selectedWorkflowKey changes
  useEffect(() => {
    if (selectedWorkflowKey) {
      loadVersions();
    } else {
      setVersions([]);
      setLoading(false);
    }
  }, [selectedWorkflowKey, loadVersions]);

  // Auto-refresh interval
  useEffect(() => {
    if (!enableAutoRefresh || refreshInterval <= 0 || !selectedWorkflowKey) {
      return undefined;
    }

    const interval = setInterval(() => {
      loadVersions();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [enableAutoRefresh, refreshInterval, selectedWorkflowKey, loadVersions]);

  return {
    versions,
    loading,
    error,
    selectedWorkflowKey,
    setSelectedWorkflowKey,
    refresh,
    repository,
  };
}
